import * as React from "react";
import {useEffect, useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {isTruthy} from "@khanacademy/wonder-stuff-core";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {useRegionOverride} from "@khan/data-region-override";
import {doNotTranslateYet} from "@khan/i18n";
import Log, {Errors} from "@khan/logging";
import {RequestInfo} from "@khan/shared-core";
import {useLocation} from "@khan/urls";

import NotFound from "#app-shell-package/errors/not-found.tsx";
import PageRoot from "#app-shell-package/page-root.tsx";
import RouteLoadSpinner from "#app-shell-package/route-load-spinner.tsx";

import type {GetSetOfStandards_setOfStandards as GetSetOfStandards} from "./__graphql-types__/GetSetOfStandards.ts";
import {
    getStandardSubTreeFromSet,
    transformSetOfStandardsToTree,
} from "./helpers/transform-set-of-standards-to-tree.ts";
import {withStrings} from "./locales/standards-mapping-page/load-strings.tsx";
import {StandardsMappingContent} from "./standards-mapping-content.tsx";
import type {
    Coverage,
    SetOfStandards,
    Standard,
    StandardMappedContentItem,
} from "./types.ts";
import {useSetOfStandardsQuery} from "./use-set-of-standards-query.ts";

type Props = {
    setOfStandardsId: string;
    standardId?: string | null | undefined;
};

// useSetOfStandardsQuery is provided with the wildcard value here to prevent filtering standards by the user's region
// (i.e. we want to show all standards, not just the ones for the user's region)
const WILDCARD_REGION_FILTER = "*";

/**
 * Convert GraphQL type to shared type for use in
 * transformSetOfStandardsToTree().
 */
const mapSetOfStandards = (
    setOfStandards?: GetSetOfStandards | null,
): SetOfStandards | null | undefined => {
    if (
        !setOfStandards ||
        !setOfStandards.id ||
        !setOfStandards.id.length ||
        !setOfStandards.name ||
        !setOfStandards.name.length ||
        !setOfStandards?.standardRegionIds?.length
    ) {
        return null;
    }

    let standards: Array<Standard | null | undefined> = [];
    if (setOfStandards.standards) {
        standards = setOfStandards.standards
            .filter(
                (standard) =>
                    standard &&
                    standard.id &&
                    standard.standardId &&
                    standard.description &&
                    standard.children,
            )
            .map((standard) => {
                let children: Array<string> = [];
                if (standard && standard.children) {
                    children = standard.children.filter(isTruthy);
                }
                let mappedContent: Array<StandardMappedContentItem> = [];
                if (standard && standard.mappedContent) {
                    mappedContent = standard.mappedContent.filter((item) => {
                        if (!item) {
                            return false;
                        }

                        if (
                            item.__typename === "Course" ||
                            item.__typename === "Unit" ||
                            item.__typename === "Lesson" ||
                            item.__typename === "Topic"
                        ) {
                            return false;
                        }

                        if (
                            item.__typename === "Exercise" &&
                            item.interimAssessmentType
                        ) {
                            return false;
                        }

                        return (
                            item.id &&
                            item.contentKind &&
                            item.title &&
                            item.defaultUrlPath &&
                            item.urlWithinStandardSet
                        );
                    }) as Array<StandardMappedContentItem>;
                }
                return {
                    ...standard,
                    coverage: mapCoverage(standard?.coverage ?? null),
                    children: children,
                    groupLabel: standard?.groupLabel || "",
                    mappedContent,
                } as Standard;
            });
    }

    return {
        ...setOfStandards,
        // Note: filtering out ?string types, in reality I think this type
        // should be an array of string (non-null) in GraphQL
        standardRegionIds: setOfStandards.standardRegionIds?.filter(isTruthy),
        domains: setOfStandards.domains,
        standards,
    } as SetOfStandards;
};

const mapCoverage = (coverage: string | null): Coverage | null => {
    switch (coverage) {
        case "fully":
            return "fully";
        case "mostly":
            return "mostly";
        case "partially":
            return "partially";
        case "not":
            return "not";
        case null:
            return null;
    }

    Log.error(`Unexpected coverage string: ${coverage}`, Errors.Internal);
    return null;
};

const StandardsMappingPage = (props: Props): React.ReactElement => {
    const {setOfStandardsId, standardId} = props;

    const {hash, pathname, search} = useLocation();
    const result = useSetOfStandardsQuery(
        setOfStandardsId,
        WILDCARD_REGION_FILTER,
    );

    const regionOverrideResult = useRegionOverride();
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    // Note: this is in state so that it's possible to navigate around on the
    // page without needing to add this query param to all links. A full-page
    // reload is required to clear this state.
    const [includeUnmapped] = useState(
        new URLSearchParams(search).has("include-unmapped"),
    );

    useEffect(() => {
        if (regionOverrideResult.status !== "success") {
            return;
        }
        const referrerHost = document.referrer.length
            ? new URL(document.referrer).hostname
            : null;

        // If a user visists a top level standards set page like /standards/CCSS.Math
        // there will be no standardId
        const standardIdOrEmptyString = standardId ?? "";
        publishAnalyticsEvent({
            eventType: "StandardsMappingPageViewed",
            eventSchemaVersion: 2,
            pageStandardId: standardIdOrEmptyString,
            pageStandardSetId: setOfStandardsId,
            referrerDomain: referrerHost,
            standardRegionUserContext_ipGeoRegionCode: RequestInfo.kaRegion,
            standardRegionUserContext_regionCodeSource:
                regionOverrideResult.regionSource,
            standardRegionUserContext_regionCodeUsed:
                regionOverrideResult.regionCode,
        });
    }, [
        publishAnalyticsEvent,
        regionOverrideResult,
        standardId,
        setOfStandardsId,
    ]);

    // TODO(starin): The designs call for this to be inside of the header
    // and centered on the page
    if (result.status === "loading" || result.status === "no-data") {
        return <RouteLoadSpinner />;
    }

    // TODO(starin): According to the designs this should be a loading failed
    // page
    if (result.status !== "success" || !result.data.setOfStandards) {
        return <NotFound />;
    }

    const setOfStandards = mapSetOfStandards(result.data.setOfStandards);
    if (!setOfStandards) {
        return <NotFound />;
    }

    const standardRegionIdsRaw =
        result?.data?.setOfStandards?.standardRegionIds;
    // Keep TypeScript happy
    const standardRegionIds = standardRegionIdsRaw?.filter(isTruthy);

    const setTrees = transformSetOfStandardsToTree(setOfStandards, {
        includeUnmapped,
    });
    if (setTrees == null || setTrees.length === 0) {
        return <NotFound />;
    }

    const subTreeToShow = standardId
        ? getStandardSubTreeFromSet(standardId, setTrees)
        : undefined;

    const setName =
        result.data.setOfStandards?.name ??
        doNotTranslateYet`Unknown Standard Set`;
    const pageTitle = doNotTranslateYet`Standards Mapping - ${setName}`;
    const scrollToStandardId = hash ? hash.substr(1) : undefined;

    return (
        <PageRoot
            settings={{
                noFollow: !setOfStandards.isDiscoverable,
                noIndex: !setOfStandards.isDiscoverable,
                title: pageTitle,
                canonicalUrl: pathname,
            }}
        >
            <View style={styles.page}>
                <StandardsMappingContent
                    scrollToStandardId={scrollToStandardId}
                    subTreeToShow={subTreeToShow}
                    setName={setName}
                    setTrees={setTrees}
                    setDomains={setOfStandards.domains}
                    standardRegionIds={standardRegionIds}
                />
            </View>
        </PageRoot>
    );
};

const styles = {
    page: {
        backgroundColor: color.offWhite,
    },
} as const;

export default withStrings(StandardsMappingPage);
